import React from 'react';
import { Box, Button, Card, Checkbox, Flex, Icon, Text, TextInput } from 'pounceV2';
import useModalV3 from 'Hooks/useModalV3';
import { FieldFilterTypes } from 'Components/forms/DetectionFieldFiltersForm/constants';
import { useTheme } from '@mui/material';
import MultiValueItemV3 from './MultiValueItemV3';
import useMultiValueReducer, { checkDuplicates } from './useMultiValueReducer';
import MultiValueItemEditV3 from './MultiValueItemEditV3';

export interface MultiValueModalProps<T> {
  initialItems: T[];
  onSubmit: (values: T[]) => void;
  /**
   * A function that runs before a custom item is added by the user. If it returns `true`, then this
   * item will be added to the selection. If not, then this item won't be added
   * */
  validateAddition?: (userEnteredInput: T, existing: T[], splitOnComma: boolean) => boolean;
  arrayType?: FieldFilterTypes;
}

function MultiValueModalV3<T extends React.ReactNode = any>({
  initialItems = [],
  onSubmit,
  validateAddition = () => true,
  arrayType = FieldFilterTypes.STRING_ARRAY,
}: MultiValueModalProps<T>) {
  const { hideModal } = useModalV3();
  const [splitOnComma, setSplitOnComma] = React.useState(true);
  const {
    palette: { tokens },
  } = useTheme();
  const {
    error,
    activeItemIndex,
    inputValue,
    items,
    addItems,
    updateItem,
    deleteItem,
    clearItems,
    setInputValue,
    editItem,
    cancelEdit,
  } = useMultiValueReducer({ initialItems, validateAddition, splitOnComma });
  const onApply = React.useCallback(() => {
    onSubmit(
      (arrayType === FieldFilterTypes.INT_ARRAY || arrayType === FieldFilterTypes.FLOAT_ARRAY
        ? items.map(item => Number(item))
        : items) as T[],
    );
    hideModal();
  }, [onSubmit, arrayType, items, hideModal]);

  const onAdd = React.useCallback(() => {
    // The line below enables users to entry a comma delimited string that would be split to different entries
    const newItems = (
      splitOnComma ? inputValue.split(',').map(x => x.trim()) : [inputValue]
    ) as T[];
    addItems(newItems);
  }, [splitOnComma, inputValue, addItems]);

  const onChange = React.useCallback(
    ({ target: { value } }) => {
      setInputValue(value);
    },
    [setInputValue],
  );

  const validateEditItem = React.useCallback(
    item => {
      const val = (item || '').trim();

      const isDuplicate = checkDuplicates(val, items);
      if (isDuplicate) {
        return 'Value exists already';
      }
      if (val !== '' && !validateAddition(val, items, splitOnComma)) {
        return 'Invalid value';
      }
      return null;
    },
    [items, splitOnComma, validateAddition],
  );

  const handleSubmit = React.useCallback(
    event => {
      event.preventDefault();
      onAdd();
      return false;
    },
    [onAdd],
  );

  return (
    <Flex direction="column" width={650} minHeight={600} overflow="auto" justify="space-between">
      <Box>
        <Flex gap={4} as="form" onSubmit={handleSubmit}>
          <Box width={0.9}>
            <TextInput
              label="Enter value(s)"
              value={inputValue}
              onChange={onChange}
              aria-label="Add value or multiple values at a time"
              data-tid="add-values-input"
            />
          </Box>
          <Button
            variant="outline"
            disabled={!inputValue?.length || Boolean(error)}
            onClick={onAdd}
          >
            Add
          </Button>
        </Flex>
        {error && (
          <Text color={tokens.content.text.semantic.error.base} variant="body/sm" pt={1} pl={2}>
            {error}
          </Text>
        )}
        <Flex alignItems="center" mt={1}>
          <Checkbox
            checked={splitOnComma}
            aria-label={splitOnComma ? 'unselect item' : 'select item'}
            onChange={isChecked => setSplitOnComma(isChecked.target.checked)}
            data-testid="split-on-comma-checkbox"
            size="small"
            label="Values entered above are comma-delimited."
          />
        </Flex>

        <Box mt={4}>
          {items.length ? (
            <Card
              display="flex"
              justifyContent="space-between"
              px={4}
              py={2}
              my={2}
              alignItems="center"
              borderWidth={0}
            >
              <Text variant="body/sm">{`${items.length} Value${items.length > 1 ? 's' : ''}`}</Text>
              <Button
                onClick={() => clearItems()}
                variant="link-primary"
                startIcon={<Icon type="close-circle" size="small" />}
                size="small"
                p={0}
              >
                Clear All
              </Button>
            </Card>
          ) : null}
          <Box maxHeight="400px" overflowY="auto">
            {items.map((item, i) =>
              i === activeItemIndex ? (
                <MultiValueItemEditV3<T>
                  key={i}
                  initialValue={item}
                  onCancel={cancelEdit}
                  onSave={updateItem}
                  validateItem={validateEditItem}
                />
              ) : (
                <MultiValueItemV3<T> key={i} value={item} onEdit={editItem} onDelete={deleteItem} />
              ),
            )}
          </Box>
        </Box>
      </Box>
      <Flex justify="center" my={4}>
        <Button onClick={onApply} disabled={initialItems === items}>
          Apply
        </Button>
      </Flex>
    </Flex>
  );
}

export default MultiValueModalV3;
